import React, { useMemo } from 'react';
import { Box } from '@apia/theme';
import useContext from '../../../../hooks/useContext';
import MakeField from '../fieldsStore/MakeField';
import FieldContext from '../fieldsStore/FieldContext';
import { IApiaSelect, TApiaSelectPossibleValue } from '../../../../types';
import IProperty from '../../../../JSApi/constants/IProperty';
import { getFieldErrorStyles } from '../style';
import { arrayOrArray } from '../../../../utils/util';
import { getSelectedValues } from './ApiaMultiple';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { useApiaFormContext } from '../page/ApiaForm';
import Autocomplete from '../../../common/fields/Autocomplete';
import { TId } from '../../../common/fields/Autocomplete/types';
import { formatMessage } from '@apia/util';

function searchSelectedValue(values: TApiaSelectPossibleValue[]) {
  const value = values.find((current) => current.selected);
  return value;
}

const ApiaSelectRenderer = React.forwardRef<HTMLInputElement>((_, ref) => {
  const {
    state: { isValid, value: currentValue },
    setValue,
    fireEvent,
  } = useContext(FieldContext);
  const { fieldId } = useContext(ApiaComponentContextPrimitive);
  const props = usePropsSelector(fieldId);
  const field = props.field as IApiaSelect;
  const { attLabel, attName, properties } = field;

  const { name } = properties;
  const handleChange = React.useCallback(
    (ev: TId) => {
      setValue(ev);
      fireEvent('onChange');
    },
    [fireEvent, setValue],
  );

  const { identifier, isReadonly } = useApiaFieldCommonProps({
    className: 'select__field',
  });

  const possibleValues = useMemo(() => {
    const arr = [
      ...arrayOrArray(
        props.possibleValue ?? (props.field as IApiaSelect).possibleValue,
      ),
    ];
    if (!arr[0] || (arr[0].value === '' && !arr[0].label))
      arr[0] = { ...arr[0], label: '' };
    return arr;
  }, [props.field, props.possibleValue]);
  const options = useMemo(() => arrayOrArray(possibleValues), [possibleValues]);
  const defaultOption = useMemo(
    () => options.find((current) => current.value === currentValue),
    [currentValue, options],
  );
  const containerProps = useMemo(
    () => ({
      'data-id': fieldId,
      ...identifier,
    }),
    [identifier, fieldId],
  );
  const inputProps = useMemo(
    () => ({
      sx: {
        ...getFieldErrorStyles(isValid),
      },
      variant: 'forms.select',
      name: name || attName,
      ref,
      'aria-label':
        props[IProperty.PROPERTY_ARIA_LABEL] ||
        props[IProperty.PROPERTY_TOOLTIP] ||
        attLabel,
      'aria-description': props[IProperty.PROPERTY_ARIA_DESCRIPTION],
      title:
        props[IProperty.PROPERTY_TITLE] ||
        props[IProperty.PROPERTY_TOOLTIP] ||
        attLabel,
      autocomplete: props[IProperty.PROPERTY_AUTO_COMPLETE],
      autofocus: props[IProperty.PROPERTY_AUTO_FOCUS],
    }),
    [attLabel, attName, isValid, name, props, ref],
  );

  const { isFormReadOnly } = useApiaFormContext();
  if (isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) {
    const readonlyValue =
      possibleValues.find(
        (current) => current.value === (currentValue as string),
      )?.label ??
      searchSelectedValue(possibleValues)?.label ??
      currentValue;

    return <Box {...identifier}>{readonlyValue as string}</Box>;
  }
  return (
    <Autocomplete
      color={props[IProperty.PROPERTY_VALUE_COLOR]}
      containerProps={containerProps}
      disabled={props[IProperty.PROPERTY_DISABLED]}
      id={fieldId}
      inputProps={inputProps}
      initialOptions={options}
      onChange={handleChange}
      readonly={isReadonly}
      value={currentValue as string}
      defaultOption={defaultOption}
      showSpinner={options.length > 100}
    />
  );
});

export function apiaSelectGetInitialValue(
  possibleValue?: TApiaSelectPossibleValue | TApiaSelectPossibleValue[],
) {
  const value = getSelectedValues(possibleValue ?? []);
  if (!value || (Array.isArray(value) && value.length === 0))
    return arrayOrArray(possibleValue ?? { value: '', label: '' })[0].value;
  return value[0];
}

const ApiaSelect = MakeField(ApiaSelectRenderer, {
  getInitialValue({ context: { field } }) {
    const { possibleValue } = field as IApiaSelect;
    return apiaSelectGetInitialValue(possibleValue);
  },
  doValidate(tools) {
    const value = tools.fieldContext.state.value;
    const required = tools.fieldContext.props?.required;
    if (required && [undefined, ''].includes(value as any)) {
      tools.fieldContext.setError(
        formatMessage(window.MSG_REQ_FIELD, { TOK1: '' }),
      );
      return false;
    }
    tools.fieldContext.setError(null);
    return true;
  },
});

export default ApiaSelect;
